import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { FileText, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';

const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.dwg,.dxf,.zip';

interface FileDropzoneProps {
    files: File[];
    onFilesChange: (files: File[]) => void;
    maxFiles?: number;
    /** Per-file limit in MB, matched to the server's validation rule. */
    maxSizeMb?: number;
    disabled?: boolean;
    className?: string;
}

function formatSize(bytes: number): string {
    const units = ['B', 'KB', 'MB'];
    let size = bytes;
    let unit = 0;
    while (size >= 1024 && unit < units.length - 1) {
        size /= 1024;
        unit += 1;
    }

    return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

/**
 * Drag-and-drop file picker for the drawings and documents attached to a request.
 *
 * Enforces the same count and size limits as the server rule so an oversized file is
 * rejected before it costs an upload round-trip.
 */
export function FileDropzone({ files, onFilesChange, maxFiles = 10, maxSizeMb = 20, disabled = false, className }: FileDropzoneProps) {
    const { t } = useTranslations();
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragActive, setDragActive] = useState(false);
    const [rejected, setRejected] = useState<string[]>([]);

    const add = (incoming: FileList | File[] | null) => {
        if (!incoming) {
            return;
        }

        const tooLarge: string[] = [];
        const accepted: File[] = [];

        for (const file of Array.from(incoming)) {
            if (file.size > maxSizeMb * 1024 * 1024) {
                tooLarge.push(file.name);

                continue;
            }
            accepted.push(file);
        }

        setRejected(tooLarge);
        onFilesChange([...files, ...accepted].slice(0, maxFiles));
    };

    const remove = (index: number) => {
        onFilesChange(files.filter((_, i) => i !== index));
    };

    const atCapacity = files.length >= maxFiles;

    return (
        <div className={cn('space-y-2', className)}>
            <input
                ref={inputRef}
                type="file"
                multiple
                className="hidden"
                accept={ACCEPT}
                disabled={disabled}
                onChange={(event) => {
                    add(event.target.files);
                    // Reset so picking the same file twice still fires a change.
                    event.target.value = '';
                }}
            />

            <div
                role="button"
                tabIndex={disabled || atCapacity ? -1 : 0}
                aria-disabled={disabled || atCapacity}
                className={cn(
                    'rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors',
                    dragActive ? 'border-primary bg-primary/5' : 'border-muted',
                    disabled || atCapacity ? 'cursor-not-allowed opacity-60' : 'hover:bg-muted/50 cursor-pointer',
                )}
                onDragOver={(event) => {
                    event.preventDefault();
                    if (!disabled && !atCapacity) {
                        setDragActive(true);
                    }
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(event) => {
                    event.preventDefault();
                    setDragActive(false);
                    if (!disabled && !atCapacity) {
                        add(event.dataTransfer.files);
                    }
                }}
                onClick={() => {
                    if (!disabled && !atCapacity) {
                        inputRef.current?.click();
                    }
                }}
                onKeyDown={(event) => {
                    if ((event.key === 'Enter' || event.key === ' ') && !disabled && !atCapacity) {
                        event.preventDefault();
                        inputRef.current?.click();
                    }
                }}
            >
                <Upload className="text-muted-foreground mx-auto mb-2 h-6 w-6" />
                <p className="text-sm font-medium">
                    {atCapacity
                        ? t('calculation_requests.attachments.dropzone_full', 'File limit reached')
                        : t('calculation_requests.attachments.dropzone_headline', 'Drop files here')}
                </p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                    {t('calculation_requests.attachments.dropzone_hint', 'or click to browse — up to :count files, :size MB each')
                        .replace(':count', String(maxFiles))
                        .replace(':size', String(maxSizeMb))}
                </p>
            </div>

            {rejected.length > 0 ? (
                <p className="text-destructive text-xs">
                    {t('calculation_requests.attachments.dropzone_too_large', 'Too large, not added: :names').replace(':names', rejected.join(', '))}
                </p>
            ) : null}

            {files.length > 0 ? (
                <ul className="space-y-1.5">
                    {files.map((file, index) => (
                        <li key={`${file.name}-${index}`} className="flex items-center gap-2 rounded-md border px-2.5 py-1.5">
                            <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                            <span className="min-w-0 flex-1 truncate text-sm">{file.name}</span>
                            <span className="text-muted-foreground shrink-0 text-xs">{formatSize(file.size)}</span>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 shrink-0"
                                onClick={() => remove(index)}
                                aria-label={t('calculation_requests.attachments.delete', 'Delete')}
                            >
                                <X className="h-3.5 w-3.5" />
                            </Button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}
